<template>
  <PageWrap
    :class="[
      $style.base,
      {
        [$style.smallScreen]: $screen === 's'
      }
    ]"
    :heading="$t('admin.nav.links.general')"
    :loading="loading"
    useForm
    @submit="submitForm"
  >
    <Flex
      direction="column"
      :gap="2"
    >
      <BaseCard>
        <Flex wrap>
          <div :class="$style.splitRow">
            <BaseDropdown
              v-model="formData.language"
              :label="$t('admin.general_settings.form.language')"
              :options="languageOptions"
              :info="$t('global.language_auto_translated_info')"
              v-test="'general-settings-language'"
            />
            <BaseDropdown
              v-model="formData.currency"
              :label="$t('admin.general_settings.form.currency')"
              :options="currencyOptions"
              v-test="'general-settings-currency'"
            />
          </div>

          <div :class="$style.splitRow">
            <BaseDropdown
              v-model="formData.timeZone"
              :label="$t('admin.general_settings.form.time_zone')"
              :options="timezoneOptions"
              v-test="'general-settings-time-zone'"
            />
            <div :class="$style.splitRow">
              <BaseRadio
                v-model="formData.settings.locale.firstDayOfWeek"
                :label="$t('admin.general_settings.form.week_start')"
                :options="[
              {
                label: $t('global.day_names.mon'),
                value: 'MONDAY'
              },
              {
                label: $t('global.day_names.sun'),
                value: 'SUNDAY'
              }
            ]"
                v-test="'general-settings-week-starts-on'"
              />
              <BaseRadio
                v-model="formData.settings.locale.clock"
                :label="$t('admin.general_settings.form.time_format')"
                :options="[
              {
                label: '13:15',
                value: 'MILITARY'
              },
              {
                label: '1:15 PM',
                value: 'REGULAR'
              }
            ]"
                v-test="'general-settings-time-format'"
              />
            </div>
          </div>
        </Flex>
      </BaseCard>

      <BaseCard :heading="$t('admin.user_accounts.modal.permissions.title')">
        <Flex direction="column">
          <BaseCard gray>
            <BaseCheckbox
              v-model="formData.settings.general.allowImpersonation"
              :label="$t('admin.general_settings.form.allow_impersonation', brandName())"
              :description="$t('admin.general_settings.form.impersonation_hint', brandName())"
              v-test="'general-settings-allow-impersonation'"
            />
          </BaseCard>

          <BaseCard gray>
            <Flex direction="column">
              <BaseCheckbox
                v-model="hasAllowedIps"
                :label="$t('admin.general_settings.form.ip_restriction')"
                :description="$t('admin.general_settings.form.ip_restriction_hint')"
                v-test="'general-settings-restrict-ips'"
              />
              <div
                v-if="hasAllowedIps"
                :class="$style.ipContainer"
              >
                <div
                  v-for="(ip, index) in formData.settings.general.ipRestrictions"
                  :key="`${index}-allowed-ip`"
                >
                  <div :class="$style.ipInputContainer">
                    <BaseInput
                      v-model="formData.settings.general.ipRestrictions[index]"
                      :label="`IP #${index + 1}`"
                    />
                    <div
                      v-if="formData.settings.general.ipRestrictions.length > 1"
                      :class="$style.delete"
                    >
                      <BaseIcon
                        name="close"
                        @click="removeIp(index)"
                        v-test="`general-settings-ip-${index + 1}-remove`"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <BaseText
                v-if="hasAllowedIps"
                iconBefore="plus"
                inline
                @click="addIp"
              >
                {{ $t('admin.general_settings.form.add_ip') }}
              </BaseText>
            </Flex>
          </BaseCard>
        </Flex>
      </BaseCard>

      <BaseCard
        v-if="company.country === 'nl' && hasFeatureFlag('module-register')"
        :heading="$t('admin.general_settings.advanced.title')"
      >
        <BaseCard gray>
          <BaseCheckbox
            v-model="formData.settings.features.medical"
            :label="$t('admin.general_settings.advanced.medical.label')"
            :description="$t('admin.general_settings.advanced.medical.hint')"
            v-test="'general-settings-enable-medical'"
          />
        </BaseCard>
      </BaseCard>

      <BaseText
        color="error"
        iconBefore="delete"
        inline
        @click="handleDeleteAccountClick"
        v-test="'general-settings-delete-account'"
      >
        {{ $t('admin.general_settings.delete_account') }}
      </BaseText>
    </Flex>

    <template #actions>
      <BaseButton
        :loading="isSubmitting"
        submitForm
        v-test="'general-settings-submit-button'"
        :fullWidth="$screen === 's'"
      >
        {{ $t('global.actions.save') }}
      </BaseButton>
    </template>

    <CancelSubscriptionModal
      v-if="showCancelSubscriptionModal"
      @close="showCancelSubscriptionModal = false"
    />
    <DeleteAccountModal
      v-if="showDeleteAccountModal"
      @close="showDeleteAccountModal = false"
    />
  </PageWrap>
</template>

<script
  lang="ts"
  setup
>
import { useQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import config from '@/config';
import { brandName } from '@/helpers/brand-name';
import { flash, modal } from '@/helpers/ui';
import { useCompanyStore } from '@/stores/company';
import { useLocalizationStore } from '@/stores/localization';
import { useUserStore } from '@/stores/user';
import type { Clock, Currency, FirstDayOfWeek, LocaleOptions } from '@/types';
import { CompanyLanguage } from '@/types';
import PageWrap from '../PageWrap.vue';
import CancelSubscriptionModal from './CancelSubscriptionModal.vue';
import DeleteAccountModal from './DeleteAccountModal.vue';

const { t } = useI18n();

const { company, updateCompany: updateCompanyMutation } = useCompanyStore();
const { hasFeatureFlag } = useUserStore();

const localeOptionsQuery = gql`
  query getLocaleOptions {
    localeOptions {
      currency
      timeZones
    }
  }
`;

const { result: localeOptionsResult, loading } = useQuery<{
  localeOptions: LocaleOptions;
}>(localeOptionsQuery);

const currencyOptions = computed(
  () => localeOptionsResult.value?.localeOptions.currency || [],
);
const timezoneOptions = computed(
  () => localeOptionsResult.value?.localeOptions.timeZones || [],
);

const { languageLabels } = useLocalizationStore();

const languageOptions = computed(() =>
  Object.keys(CompanyLanguage).map((key: string) => ({
    value: key.toUpperCase(),
    label: languageLabels[key.toLowerCase() as keyof typeof languageLabels],
    labelExtra: config.localesUnverified.includes(
      key.toLowerCase() as (typeof config.localesUnverified)[number],
    )
      ? t('global.language_auto_translated')
      : '',
  })),
);

const hasAllowedIps = ref(company.settings.general.ipRestrictions.length > 0);
const showDeleteAccountModal = ref(false);
const showCancelSubscriptionModal = ref(false);

interface FormData {
  language: CompanyLanguage;
  currency: Currency;
  timeZone: string;
  settings: {
    locale: {
      clock: Clock;
      firstDayOfWeek: FirstDayOfWeek;
    };
    general: {
      allowImpersonation: boolean;
      ipRestrictions: string[];
    };
    features: {
      medical: boolean;
    };
  };
}

const formData = reactive<FormData>({
  language: company.language.toUpperCase() as CompanyLanguage,
  currency: company.currency,
  timeZone: company.timeZoneName,
  settings: {
    locale: {
      clock: company.settings.locale.clock,
      firstDayOfWeek: company.settings.locale.firstDayOfWeek,
    },
    general: {
      allowImpersonation: company.settings.general.allowImpersonation,
      ipRestrictions: company.settings.general.ipRestrictions,
    },
    features: {
      medical: company.settings.features?.medical,
    },
  },
});
const isSubmitting = ref(false);

watch(hasAllowedIps, () => {
  if (
    hasAllowedIps.value &&
    formData.settings.general.ipRestrictions.length === 0
  ) {
    formData.settings.general.ipRestrictions.push('');
  }
});

const handleDeleteAccountClick = () => {
  if (company.activeSubscription) {
    showCancelSubscriptionModal.value = true;
  } else {
    showDeleteAccountModal.value = true;
  }
};

const addIp = () => {
  formData.settings.general.ipRestrictions.push('');
};

const removeIp = (index: number) => {
  formData.settings.general.ipRestrictions.splice(index, 1);
};

const updateCompany = () => {
  isSubmitting.value = true;
  updateCompanyMutation(formData)
    .then(() => {
      flash(t('global.flash.company_updated'));
    })
    .finally(() => {
      isSubmitting.value = false;
    });
};

const submitForm = () => {
  if (!hasAllowedIps.value) {
    formData.settings.general.ipRestrictions = [];
  }

  formData.settings.general.ipRestrictions =
    formData.settings.general.ipRestrictions.filter((ip) => !!ip.length);

  if (formData.settings.general.ipRestrictions.length > 0) {
    modal('confirmation', {
      message: t('global.confirmation.confirm_proceed'),
      subMessage: t(
        'admin.general_settings.confirmation.ip_restriction',
        brandName(),
      ),
    }).then(() => {
      updateCompany();
    });
  } else {
    hasAllowedIps.value = false;
    updateCompany();
  }
};
</script>

<style
  lang="scss"
  module
>
.splitRow {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: $spacing;
  width: 100%;

  & > * {
    width: calc(50% - #{$spacing * 0.5});
  }

  .base.smallScreen & {
    & > * {
      width: 100%;
    }
  }
}

.formActions > *:first-child {
  margin-right: $spacing;
}

.ipContainer {
  display: flex;
  gap: $spacing;
  // margin-top: $spacing;
  flex-wrap: wrap;
}

.ipInputContainer {
  display: flex;
  align-items: center;
  gap: $spacing * 0.5;
}

.delete {
  margin-top: 25px; // Input label height + margin
}
</style>
